import { useEffect, useMemo } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'

import { fetchSubscriptions } from '@/reducers/subscription.slice'
import { fetchFinancialAccountsIfNeeded } from '@/actions/financialAccountActions'
import { fetchCurrentUserDetailsIfNeeded } from '@/actions/userActions'
import { createIsSomeEnum, useAppDispatch } from '@/utils/typeHelpers'
import { useScrollRef } from '@/utils/sharedHooks'

// These are shared by multiple features - Move to root level slice file
import { fetchUserTaxEstimates } from '@/featuresV1/Taxes/QuarterlyTaxEstimates/userTaxEstimates.slice'
import { fetchUserTransactions } from '@/featuresV1/Transactions/transactions.slice'

import { CANCELLATION_FLOW_STEP } from './lib/helpers'
import { CancellationPrevented } from './CancellationPrevented'
import { SavingMoneyAndTime } from './SavingMoneyAndTime'
import { CostComparison } from './CostComparison'
import { DiscountOffer } from './DiscountOffer'
import { DiscountComplete } from './DiscountComplete'
import { CancellationComplete } from './CancellationComplete'

const isStep = createIsSomeEnum(CANCELLATION_FLOW_STEP)

export const SelfServeCancellation = () => {
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()
  const { scrollRef, scrollToRef } = useScrollRef({ autoScroll: true })
  const location = useLocation()
  const recordId = location?.state?.recordId
  const stepParam = searchParams.get('step')

  const step = isStep(stepParam)
    ? stepParam
    : CANCELLATION_FLOW_STEP.savingMoneyAndTime

  useEffect(() => {
    scrollToRef()
  }, [scrollToRef])

  useEffect(() => {
    dispatch(fetchUserTransactions())
    dispatch(fetchUserTaxEstimates())
    dispatch(fetchFinancialAccountsIfNeeded())
    dispatch(fetchSubscriptions())
    dispatch(fetchCurrentUserDetailsIfNeeded(true))
  }, [dispatch])

  const stepCcontent = useMemo(() => {
    switch (step) {
      case CANCELLATION_FLOW_STEP.savingMoneyAndTime:
        return <SavingMoneyAndTime recordId={recordId} />
      case CANCELLATION_FLOW_STEP.costComparison:
        return <CostComparison recordId={recordId} />
      case CANCELLATION_FLOW_STEP.discountOffer:
        return <DiscountOffer recordId={recordId} />
      case CANCELLATION_FLOW_STEP.discountComplete:
        return <DiscountComplete />
      case CANCELLATION_FLOW_STEP.cancellationComplete:
        return <CancellationComplete recordId={recordId} />
      case CANCELLATION_FLOW_STEP.cancellationPrevented:
        return <CancellationPrevented />
      default:
        return step satisfies never
    }
  }, [step, recordId])

  return (
    <div>
      <span ref={scrollRef} />
      {stepCcontent}
    </div>
  )
}
